/**
 * Book Management Module
 * 
 * Handles scanned book CRUD, library operations, and ISBN lookup.
 * Part of the shared library model where books are global content.
 */

import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
import type { ScannedBook, BookMetadata } from './types';
import { generateWorkGroup } from './work-group-utils';

// Re-export for backward compatibility (existing consumers import from here)
export { generateWorkGroup } from './work-group-utils';

// ============================================================================
// Book Scan Request Types (Human-in-the-Loop)
// ============================================================================

export interface BookScanRequest {
  id: string;
  isbn: string | null;
  title: string;
  author: string | null;
  cover_image_url: string | null;
  publisher: string | null;
  binding: string | null;
  work_group: string | null;
  requested_by: string;
  status: 'pending' | 'assigned' | 'scanning' | 'editing' | 'ready' | 'cancelled';
  assigned_to: string | null;
  assigned_at: string | null;
  resolved_book_id: string | null;
  resolved_at: string | null;
  priority: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Global Library Functions
// ============================================================================

/**
 * Find existing book by ISBN GLOBALLY in ANY status.
 * Used by scan agents to prevent duplicate book creation.
 * Unlike findBookByIsbnGlobal, this catches books that are still
 * processing, needs_review, or failed — not just ready ones.
 */
export async function findBookByIsbnAnyStatus(isbn: string): Promise<ScannedBook | null> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at')
    .eq('isbn', isbn)
    .is('deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(1);

  if (error) {
    console.error('Error finding book by ISBN (any status):', error);
    return null;
  }

  return data?.[0] || null;
}

/**
 * Find existing book by ISBN GLOBALLY (across all parents).
 * Only returns books with quality_status = 'ready'.
 * Used to enable shared content library.
 */
export async function findBookByIsbnGlobal(isbn: string): Promise<ScannedBook | null> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at')
    .eq('isbn', isbn)
    .eq('quality_status', 'ready')
    .is('deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(1);

  if (error) {
    console.error('Error finding book by ISBN globally:', error);
    return null;
  }

  return data?.[0] || null;
}

/**
 * Find existing book by TITLE GLOBALLY (across all parents).
 * Only returns books with quality_status = 'ready'.
 * Uses case-insensitive matching.
 */
export async function findBookByTitleGlobal(title: string): Promise<ScannedBook | null> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at')
    .ilike('title', title)
    .eq('quality_status', 'ready')
    .is('deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(1);

  if (error) {
    console.error('Error finding book by title globally:', error);
    return null;
  }

  return data?.[0] || null;
}

/**
 * Find existing book by ISBN for a parent (checks their library)
 * Returns the book if in parent's library, null otherwise
 */
export async function findBookByIsbn(
  parentId: string,
  isbn: string
): Promise<ScannedBook | null> {
  const { data, error } = await supabase
    .from('parent_book_library')
    .select(`
      book_id,
      scanned_books!inner (id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at)
    `)
    .eq('parent_id', parentId)
    .eq('scanned_books.isbn', isbn)
    .limit(1);

  if (error) {
    console.error('Error finding book by ISBN:', error);
    return null;
  }

  const result = data?.[0];
  if (result && 'scanned_books' in result) {
    return result.scanned_books as unknown as ScannedBook;
  }

  return null;
}

// ============================================================================
// Library Junction Table Operations
// ============================================================================

/**
 * Add a book to a parent's library.
 * Creates a junction table entry.
 */
export async function addBookToLibrary(
  parentId: string,
  bookId: string
): Promise<void> {
  const { error } = await supabase
    .from('parent_book_library')
    .upsert(
      { parent_id: parentId, book_id: bookId },
      { onConflict: 'parent_id,book_id', ignoreDuplicates: true }
    );

  if (error) {
    console.error('Error adding book to library:', error);
    throw new Error(`Failed to add book to library: ${error.message}`);
  }

  console.log(`[BookManagement] Added book ${bookId} to parent ${parentId} library`);
}

/**
 * Remove a book from a parent's library (soft delete).
 * Does NOT delete the book from the global catalog.
 */
export async function removeBookFromLibrary(
  parentId: string,
  bookId: string
): Promise<void> {
  const { error } = await supabase
    .from('parent_book_library')
    .delete()
    .eq('parent_id', parentId)
    .eq('book_id', bookId);

  if (error) {
    throw new Error(`Failed to remove book from library: ${error.message}`);
  }

  console.log(`[BookManagement] Removed book ${bookId} from parent ${parentId} library`);
}

// ============================================================================
// Book CRUD Operations
// ============================================================================

/**
 * Create a new scanned book and add to parent's library.
 * Sets contributor_id to track who originally scanned the book.
 */
export async function createScannedBook(
  parentId: string,
  title: string,
  metadata?: Partial<BookMetadata>,
  requestId?: string
): Promise<ScannedBook> {
  const workGroup = generateWorkGroup(title, metadata?.author);

  const { data, error } = await supabase
    .from('scanned_books')
    .insert({
      parent_id: parentId,
      contributor_id: parentId,
      title,
      isbn: metadata?.isbn || null,
      author: metadata?.author || null,
      cover_image_url: metadata?.coverImageUrl || null,
      publisher: metadata?.publisher || null,
      total_pages: metadata?.pageCount || null,
      binding: metadata?.binding || null,
      work_group: workGroup,
      request_id: requestId || null,
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create book: ${error.message}`);
  }

  await addBookToLibrary(parentId, data.id);
  return data;
}

/**
 * Get or create a scanned book by ISBN.
 * 
 * SHARED LIBRARY MODEL:
 * 1. If ISBN provided, check if book exists GLOBALLY
 * 2. If global book exists and is ready, add to this parent's library
 * 3. If not found, create new book (and add to library)
 */
export async function getOrCreateScannedBook(
  parentId: string,
  title: string,
  metadata?: Partial<BookMetadata>
): Promise<{ book: ScannedBook; isExisting: boolean; addedToLibrary?: boolean }> {
  if (metadata?.isbn) {
    const existingInLibrary = await findBookByIsbn(parentId, metadata.isbn);
    if (existingInLibrary) {
      console.log(`[BookManagement] Book already in parent's library: ${metadata.isbn}`);
      return { book: existingInLibrary, isExisting: true };
    }

    const globalBook = await findBookByIsbnGlobal(metadata.isbn);
    if (globalBook) {
      console.log(`[BookManagement] Found global book with ISBN ${metadata.isbn}, adding to library`);
      await addBookToLibrary(parentId, globalBook.id);
      return { book: globalBook, isExisting: true, addedToLibrary: true };
    }
  }

  const book = await createScannedBook(parentId, title, metadata);
  console.log(`[BookManagement] Created new book: ${title}`);
  return { book, isExisting: false };
}

/**
 * Get all scanned books for a parent from their library.
 */
export async function getScannedBooks(parentId: string): Promise<ScannedBook[]> {
  const { data, error } = await supabase
    .from('parent_book_library')
    .select(`
      book_id,
      added_at,
      scanned_books!inner (id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at)
    `)
    .eq('parent_id', parentId)
    .order('added_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch books: ${error.message}`);
  }

  const books = (data || []).map((entry) => {
    if ('scanned_books' in entry && entry.scanned_books) {
      return entry.scanned_books as unknown as ScannedBook;
    }
    return null;
  }).filter((b): b is ScannedBook => b !== null);

  return books;
}

/**
 * Get a single scanned book
 */
export async function getScannedBook(bookId: string): Promise<ScannedBook | null> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('id, parent_id, contributor_id, isbn, title, author, cover_image_url, publisher, total_pages, scan_completed, phase, enabled_colors, quality_status, quality_issues, reconstructed_text, reconstruction_metadata, digital_page_content, request_id, binding, work_group, created_at, updated_at')
    .eq('id', bookId)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch book: ${error.message}`);
  }

  return data;
}

/**
 * Update scanned book
 */
export async function updateScannedBook(
  bookId: string,
  updates: Partial<Pick<ScannedBook, 'title' | 'author' | 'scan_completed' | 'phase' | 'enabled_colors'>>
): Promise<ScannedBook> {
  const { data, error } = await supabase
    .from('scanned_books')
    .update(updates)
    .eq('id', bookId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update book: ${error.message}`);
  }

  return data;
}

/**
 * PERMANENTLY delete a scanned book from the global catalog.
 * ADMIN ONLY - removes from all parent libraries.
 */
export async function deleteScannedBook(bookId: string): Promise<void> {
  const { error } = await supabase
    .from('scanned_books')
    .update({ deleted_at: new Date().toISOString() } as any)
    .eq('id', bookId);

  if (error) {
    throw new Error(`Failed to archive book: ${error.message}`);
  }
  
  console.log(`[BookManagement] PERMANENTLY deleted book ${bookId} from global catalog`);
}

// ============================================================================
// ISBN Lookup
// ============================================================================

/**
 * Look up book metadata by ISBN via edge function
 */
export async function lookupIsbn(isbn: string): Promise<BookMetadata | null> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    throw new Error('Not authenticated');
  }

  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lookup-isbn`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.session.access_token}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({ isbn }),
      }
    );

    if (!response.ok) {
      console.warn('[lookupIsbn] API returned error:', response.status);
      return null;
    }

    const data = await response.json();
    
    // Handle response structure from edge function
    // Edge function returns: { success: true, book: { ...metadata } }
    if (!data.success || !data.book) {
      console.warn('[lookupIsbn] Book not found in Open Library');
      return null;
    }

    const book = data.book;
    return {
      isbn: book.isbn || isbn,
      title: book.title || 'Unknown Title',
      author: book.author || null,
      coverImageUrl: book.coverImageUrl || null,
      publisher: book.publisher || null,
      pageCount: book.pageCount || null,
      binding: book.binding || null,
    };
  } catch (error) {
    console.error('[lookupIsbn] Error:', error);
    Sentry.captureException(error, { tags: { flow: 'add_book', step: 'lookup_isbn' }, extra: { isbn } });
    return null;
  }
}

// ============================================================================
// Book Scan Requests (Human-in-the-Loop)
// ============================================================================

// ============================================================================
// Duplicate Detection (Pre-Request)
// ============================================================================

export type DuplicateCheckResult =
  | { exists: false }
  | { exists: true; type: 'ready'; bookId: string; title: string };

/**
 * Check if a READY book already exists in the global catalog by ISBN.
 *
 * Design decisions:
 * - ISBN-only matching. Same title ≠ same book (editions differ in layout/pages).
 * - Only returns `ready` books. If the book exists but is processing/needs_review,
 *   we still create a request so the parent is tracked by getRequestingParents()
 *   and gets the book + notification when it's published.
 * - No dedup against book_scan_requests. Multiple parents requesting the same book
 *   is correct — resolveMatchingScanRequests() resolves all of them on publish.
 *   Suppressing the second request would silently lose that parent.
 */
export async function checkForDuplicate(
  isbn?: string
): Promise<DuplicateCheckResult> {
  if (!isbn) return { exists: false };

  const { data } = await supabase
    .from('scanned_books')
    .select('id, title, quality_status')
    .eq('isbn', isbn)
    .eq('quality_status', 'ready')
    .is('deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(1);

  if (data && data.length > 0) {
    return { exists: true, type: 'ready', bookId: data[0].id, title: data[0].title };
  }

  return { exists: false };
}

/**
 * Create a book scan request.
 * Called when a parent requests a book that isn't in the global library.
 * Performs duplicate detection first — returns existing match info instead of creating duplicates.
 */
export async function createBookScanRequest(
  parentId: string,
  title: string,
  metadata?: {
    isbn?: string;
    author?: string;
    coverImageUrl?: string;
    publisher?: string;
    binding?: string;
  }
): Promise<BookScanRequest> {
  const workGroup = generateWorkGroup(title, metadata?.author);

  const { data, error } = await supabase
    .from('book_scan_requests')
    .insert({
      requested_by: parentId,
      title,
      isbn: metadata?.isbn || null,
      author: metadata?.author || null,
      cover_image_url: metadata?.coverImageUrl || null,
      publisher: metadata?.publisher || null,
      binding: metadata?.binding || null,
      work_group: workGroup,
      status: 'pending',
      priority: 0,
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create scan request: ${error.message}`);
  }

  console.log(`[BookManagement] Created scan request for "${title}" by parent ${parentId.slice(0, 8)}`);

  // Fire-and-forget email notification to parent
  notifyBookRequestReceived(data.id).catch((err) => {
    console.error('[BookManagement] Failed to send request notification:', err);
    Sentry.captureException(err, { tags: { flow: 'add_book', step: 'notify_request_received' }, extra: { requestId: data.id } });
  });

  return data as BookScanRequest;
}

/**
 * Send "request received" email via the book-notifications edge function.
 * Non-blocking — caller uses fire-and-forget.
 */
async function notifyBookRequestReceived(requestId: string): Promise<void> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return;

  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/book-notifications`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.session.access_token}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({ action: 'send-request-received', requestId }),
    }
  );
}

/**
 * Get all scan requests for a parent
 */
export async function getMyBookScanRequests(parentId: string): Promise<BookScanRequest[]> {
  const { data, error } = await supabase
    .from('book_scan_requests')
    .select('id, isbn, title, author, cover_image_url, publisher, requested_by, status, assigned_to, assigned_at, resolved_book_id, resolved_at, priority, notes, created_at, updated_at')
    .eq('requested_by', parentId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch scan requests: ${error.message}`);
  }

  return (data || []) as BookScanRequest[];
}

/**
 * Get all pending scan requests (for agents/admins)
 */
export async function getPendingScanRequests(): Promise<BookScanRequest[]> {
  const { data, error } = await supabase
    .from('book_scan_requests')
    .select('id, isbn, title, author, cover_image_url, publisher, requested_by, status, assigned_to, assigned_at, resolved_book_id, resolved_at, priority, notes, created_at, updated_at')
    .in('status', ['pending', 'assigned', 'scanning', 'editing'])
    .is('deleted_at', null)
    .order('priority', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch pending requests: ${error.message}`);
  }

  return (data || []) as BookScanRequest[];
}

/**
 * Assign a scan request to an agent
 */
export async function assignScanRequest(
  requestId: string,
  agentId: string
): Promise<BookScanRequest> {
  const { data, error } = await supabase
    .from('book_scan_requests')
    .update({
      assigned_to: agentId,
      assigned_at: new Date().toISOString(),
      status: 'assigned',
    })
    .eq('id', requestId)
    .eq('status', 'pending')
    .select()
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to assign request: ${error.message}`);
  }

  if (!data) {
    throw new Error('Request already claimed by another agent. Please refresh and try a different request.');
  }

  return data as BookScanRequest;
}

/**
 * Update scan request status
 */
export async function updateScanRequestStatus(
  requestId: string,
  status: BookScanRequest['status'],
  resolvedBookId?: string
): Promise<BookScanRequest> {
  const updates: Record<string, unknown> = { status };
  
  if (status === 'ready' && resolvedBookId) {
    updates.resolved_book_id = resolvedBookId;
    updates.resolved_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('book_scan_requests')
    .update(updates)
    .eq('id', requestId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update request status: ${error.message}`);
  }

  return data as BookScanRequest;
}

/**
 * Delete a pending scan request.
 * ADMIN ONLY - removes the request from the queue.
 * Only allows deletion of pending/assigned requests (not in-progress).
 */
export async function deleteScanRequest(requestId: string): Promise<void> {
  // First verify the request exists and is deletable
  const { data: request, error: fetchError } = await supabase
    .from('book_scan_requests')
    .select('id, status')
    .eq('id', requestId)
    .maybeSingle();

  if (fetchError) {
    throw new Error(`Failed to fetch request: ${fetchError.message}`);
  }

  if (!request) {
    throw new Error('Scan request not found');
  }

  // Only allow deletion of pending or assigned requests
  if (!['pending', 'assigned'].includes(request.status)) {
    throw new Error(`Cannot delete request with status "${request.status}". Only pending or assigned requests can be deleted.`);
  }

  const { error } = await supabase
    .from('book_scan_requests')
    .update({ deleted_at: new Date().toISOString() } as any)
    .eq('id', requestId);

  if (error) {
    throw new Error(`Failed to archive request: ${error.message}`);
  }

  console.log(`[BookManagement] Archived scan request ${requestId}`);
}

/**
 * Get all parents who requested a specific book (by ISBN or title)
 * Used to auto-add book to their libraries when published
 */
export async function getRequestingParents(
  isbn?: string,
): Promise<string[]> {
  if (!isbn) return [];

  const { data, error } = await supabase
    .from('book_scan_requests')
    .select('requested_by')
    .in('status', ['pending', 'assigned', 'scanning', 'editing'])
    .eq('isbn', isbn)
    .is('deleted_at', null);

  if (error) {
    console.error('Error fetching requesting parents:', error);
    return [];
  }

  return [...new Set((data || []).map(r => r.requested_by))];
}

/**
 * Reuse content from an existing book for a different edition.
 * Creates a new scanned_books row with the new ISBN/binding but copies content from the source.
 * Resolves the request and adds the book to the requesting parent's library.
 */
export async function reuseBookContent(
  sourceBookId: string,
  requestId: string,
): Promise<{ newBookId: string }> {
  // Fetch source book content
  const { data: source, error: srcErr } = await supabase
    .from('scanned_books')
    .select('title, author, publisher, cover_image_url, total_pages, digital_page_content, reconstructed_text, reconstruction_metadata, phase, enabled_colors, work_group')
    .eq('id', sourceBookId)
    .maybeSingle();

  if (srcErr || !source) throw new Error('Source book not found');

  // Fetch the request for ISBN/binding
  const { data: request, error: reqErr } = await supabase
    .from('book_scan_requests')
    .select('isbn, title, author, binding, work_group, requested_by')
    .eq('id', requestId)
    .maybeSingle();

  if (reqErr || !request) throw new Error('Request not found');

  // Create new scanned_books row with source content + new edition metadata
  const { data: newBook, error: insertErr } = await supabase
    .from('scanned_books')
    .insert({
      parent_id: request.requested_by,
      contributor_id: request.requested_by,
      title: request.title || source.title,
      author: request.author || source.author,
      isbn: request.isbn,
      publisher: source.publisher,
      cover_image_url: source.cover_image_url,
      total_pages: source.total_pages,
      digital_page_content: source.digital_page_content,
      reconstructed_text: source.reconstructed_text,
      reconstruction_metadata: source.reconstruction_metadata,
      phase: source.phase,
      enabled_colors: source.enabled_colors,
      binding: request.binding,
      work_group: source.work_group || request.work_group,
      quality_status: 'turn_codes_pending',
      scan_completed: true,
      request_id: requestId,
    })
    .select('id')
    .single();

  if (insertErr || !newBook) throw new Error(`Failed to create edition: ${insertErr?.message}`);

  return { newBookId: newBook.id };
}

/**
 * Resolve all matching scan requests when a book is published.
 * ISBN-only matching — prevents accidental cross-edition resolution.
 * Books without ISBNs must be resolved manually by agents.
 */
export async function resolveMatchingScanRequests(
  bookId: string,
  isbn?: string,
): Promise<void> {
  if (!isbn) return;

  const { error } = await supabase
    .from('book_scan_requests')
    .update({
      status: 'ready',
      resolved_book_id: bookId,
      resolved_at: new Date().toISOString(),
    })
    .in('status', ['pending', 'assigned', 'scanning', 'editing'])
    .eq('isbn', isbn);

  if (error) {
    console.error('Error resolving scan requests:', error);
  }
}
